import dayjs, {Dayjs} from 'dayjs';
import React, {useState} from 'react';
import {Popover} from '@headlessui/react';
import {useQuery, useTheme, useTranslations} from 'react-ui';
import {Calendar} from 'react-ui';

export function FastFilterButton({
    dates,
    selectedDates,
    setSelectedDates,
    label,
}: {
    dates: Dayjs[];
    selectedDates: Dayjs[];
    setSelectedDates: (dates: Dayjs[]) => void;
    label: string;
}) {
    const {primaryColor} = useTheme();

    function isSelected() {
        if (selectedDates.length !== dates.length) return false;
        for (let i = 0; i < dates.length; i++) {
            if (!dates[i].isSame(selectedDates[i], 'day')) return false;
        }
        return true;
    }

    return (
        <button
            style={{
                backgroundColor: isSelected() ? primaryColor : 'white',
                color: isSelected() ? 'white' : 'black',
                padding: '6px 8px',
            }}
            className="shadow rounded-full text-sm font-medium transition-colors duration-200 ease-in-out"
            onClick={
                isSelected()
                    ? () => setSelectedDates([])
                    : () => setSelectedDates(dates)
            }
        >
            {label}
        </button>
    );
}

export function CalendarPicker({
    selectedDates,
    setSelectedDates,
    highlight,
    embedded = false,
}: {
    selectedDates: Dayjs[];
    setSelectedDates: (dates: Dayjs[]) => void;
    highlight: boolean;
    embedded?: boolean;
}) {
    const {primaryColor} = useTheme();
    const {translated} = useTranslations();
    const { removeQueryById, has } = useQuery();
    const clearDates = () => {
        setSelectedDates([]);
        removeQueryById('dates');
    }
    function onClick(clickedDate: Dayjs) {
        let base = selectedDates;
        if (base.length >= 2) {
            base = [];
        }
        if (
            selectedDates.length === 1 &&
            selectedDates[0].isSame(clickedDate, 'day')
        ) {
            setSelectedDates([]);
        } else {
            setSelectedDates(
                [...base, clickedDate].sort((a, b) => a.unix() - b.unix())
            );
        }
    }
    return (
        <Popover className="calendar-popover relative">
            <Popover.Button
                className={`calendar-button p-2 flex items-center justify-center relative ${
                    embedded
                        ? 'rounded-full hover:bg-gray-100'
                        : `bg-white shadow-lg rounded-full mx-0 mt-2 ${highlight ? 'outline outline-2' : ''}`
                }`}
                style={{outlineColor: !embedded && highlight ? primaryColor : 'transparent'}}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke={embedded && highlight ? primaryColor : 'currentColor'}
                    className="w-6 h-6 text-gray-600 md:w-7 md:h-7"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                    />
                </svg>
                {selectedDates.length > 0 && (
                    <span
                        className="absolute bottom-[-3px] right-[-3px] flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded-full"
                        style={{backgroundColor: '#AC2475'}}
                    >
                        {selectedDates.length}
                    </span>
                )}
            </Popover.Button>
            <Popover.Panel className="calendar-panel absolute z-[100] w-[390px] left-[-1rem] top-16">
                {({close}) => (
                    <div className="flex items-center justify-center py-0">
                        <div className="w-full shadow-lg">
                            <div className=" bg-white rounded-lg">
                                <div className="md:px-8 md:pb-8 p-5 pt-8 bg-white rounded-lg flex flex-col">
                                    <div className={`flex w-full justify-center mb-8 gap-2`}>
                                        <FastFilterButton
                                            dates={[dayjs()]}
                                            label={translated('calendar_today')}
                                            selectedDates={selectedDates}
                                            setSelectedDates={setSelectedDates}
                                        />
                                        <FastFilterButton
                                            dates={[dayjs(), dayjs().add(7, 'days')]}
                                            label={`7 ${translated('calendar_days')}`}
                                            selectedDates={selectedDates}
                                            setSelectedDates={setSelectedDates}
                                        />
                                        <FastFilterButton
                                            dates={[dayjs(), dayjs().add(30, 'days')]}
                                            label={`30 ${translated('calendar_days')}`}
                                            selectedDates={selectedDates}
                                            setSelectedDates={setSelectedDates}
                                        />
                                        <FastFilterButton
                                            dates={[dayjs(), dayjs().add(90, 'days')]}
                                            label={`90 ${translated('calendar_days')}`}
                                            selectedDates={selectedDates}
                                            setSelectedDates={setSelectedDates}
                                        />
                                    </div>
                                    <Calendar
                                        selectedDates={[selectedDates.map((d) => d.toDate())]}
                                        onClickDate={(d) => onClick(dayjs(d))}
                                    />
                                    <button disabled={!has('dates')} className={"self-end inline-flex mt-[1rem] justify-center rounded-full border border-transparent px-4 py-2 text-sm font-medium text-[#F4E3F7] bg-[#BF2088] hover:text-[#BF2088] hover:bg-[#F4E3F7] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#BF2088] focus-visible:ring-offset-2"} onClick={clearDates}>{translated('clear')}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Popover.Panel>
        </Popover>
    );
}
